import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Check, ArrowRight, Palette } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import SEO, { createServiceSchema } from '../components/SEO';

const paintServices = [
  "Full vehicle resprays",
  "Custom colour matching",
  "Metallic and pearl finishes",
  "Two-tone paint jobs",
  "Racing stripes and graphics",
  "Clear coat restoration",
  "Spot repairs and blending",
  "Show-quality finishing"
];

const CustomPaintPage = () => {
  const { isDark } = useTheme();

  const serviceSchema = createServiceSchema(
    'Car Spray Painting & Custom Paint Work',
    'Professional car spray painting, resprays, custom paint work, metallic finishes, and colour matching in Murray Bridge, South Australia.',
    'https://www.jprcustoms.com.au/custom-paint'
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-neutral-950' : 'bg-white'}`}>
      <SEO 
        title="Car Spray Painting & Custom Paint Work"
        description="Professional car spray painting in Murray Bridge SA. Full resprays, custom colours, metallic & pearl finishes, two-tone paint, racing stripes, clear coat restoration. Spectro-colour matching. Call +61 417 119 544."
        keywords="car spray painting, spray paint car, car respray, custom paint, metallic paint, pearl finish, two-tone paint, colour matching, automotive paint, paint resprays Murray Bridge"
        canonical="/custom-paint"
        structuredData={serviceSchema}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1512080482556-ea648017576c?w=1920&q=80)` }}
          >
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-neutral-950/95 via-neutral-950/80 to-neutral-950/60' : 'bg-gradient-to-r from-white/95 via-white/85 to-white/70'}`} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 pt-44">
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${isDark ? 'text-white' : 'text-neutral-900'}`}>
              Paint Resprays &<br />
              <span className="text-lime-500">Custom Paint Work</span>
            </h1>
            <p className={`text-xl max-w-2xl mb-8 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
              Transform your vehicle with our professional paint services and custom finishes.
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              <Phone className="w-5 h-5" />
              Get a Free Quote
            </a>
          </div>
        </section>

        {/* Content */}
        <section className={`py-20 ${isDark ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-lime-500 mb-6">Expert Paint Services</h2>
                <p className={`mb-6 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  Our state-of-the-art paint booth and advanced colour matching technology ensure a flawless finish every time.
                </p>
                <p className={`mb-8 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  We use only premium automotive paints and clear coats for lasting durability and a show-quality shine.
                </p>
                <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Our Paint Services Include:</h3>
                <ul className="space-y-3">
                  {paintServices.map((service, index) => (
                    <li key={index} className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                      <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-6">
                <div className={`p-8 rounded-xl ${isDark ? 'bg-neutral-800' : 'bg-white shadow-lg'}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <Palette className="w-10 h-10 text-lime-500" />
                    <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Spectro-Colour Matching</h3>
                  </div>
                  <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>
                    Our advanced colour matching system ensures perfect colour accuracy.
                  </p>
                </div>
                <img 
                  src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&q=80" 
                  alt="Custom paint work"
                  className="w-full h-72 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={`py-16 ${isDark ? 'bg-neutral-800' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Ready for a New Look?</h2>
            <p className={`mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>Get a free quote for your paint project today.</p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              Call {companyInfo.phone}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CustomPaintPage;
